import { useCallback, useEffect, useState } from "react";
import { api, ApiError, staleCopy } from "../api";
import type { MemoryEntry, ProjectDetail, ProjectSummary } from "../types";
import { MenuIcon } from "../icons";
import { StaleNotice } from "../components/StaleNotice";
import { fetchGraph, ProjectGraph } from "./ProjectGraph";
import type { GraphView } from "../components/graphLayout.ts";
import { researchPageHref } from "../../../shared/wire/research.ts";

function when(ts: number): string {
	return new Date(ts).toLocaleString("zh-CN", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

/**
 * One project: the direction its owner confirmed, the state the agent keeps, the log behind it,
 * and its conversations. The direction and the state are editable here because they are the two
 * things a person can put right when the agent got them wrong; the log is history and stays read-only.
 * Each is saved on its own, over the version it was loaded at, so saving one never discards an
 * unsaved edit of the other and never overwrites what the agent wrote in between.
 */
export function Project({
	id,
	onMenu,
	onOpenSession,
	onNewSession,
	onChanged,
	onOpenResearch,
}: {
	id: string;
	onMenu: () => void;
	onOpenSession: (sessionId: string) => void;
	onNewSession: (profile: string, projectId: string) => void;
	/** Title, direction or archived state changed: the sidebar lists projects and has to follow. */
	onChanged: () => void;
	/** Opens the research page on this project, on its records or its map; absent when its profile has no research tools. */
	onOpenResearch?: (view?: "map") => void;
}) {
	const [detail, setDetail] = useState<ProjectDetail | null>(null);
	const [direction, setDirection] = useState<Record<string, string>>({});
	const [state, setState] = useState("");
	const [directionDirty, setDirectionDirty] = useState(false);
	const [stateDirty, setStateDirty] = useState(false);
	/** The versions the two editors started from. */
	const [directionBase, setDirectionBase] = useState("");
	const [stateBase, setStateBase] = useState("");
	const [directionStale, setDirectionStale] = useState<{ current: ProjectSummary | null } | null>(null);
	const [stateStale, setStateStale] = useState<{ current: MemoryEntry | null } | null>(null);
	const [busy, setBusy] = useState<"direction" | "state" | "archive" | null>(null);
	const [error, setError] = useState<string | null>(null);
	/** The knowledge graph's entry: its counts, why it failed, or nothing when the project has none. */
	const [graph, setGraph] = useState<{ view: GraphView } | { error: string } | null>(null);
	const [showGraph, setShowGraph] = useState(false);
	/** The project's research records: how many, and how many wait on the owner. */
	const [research, setResearch] = useState<{ records: number; pending: number } | { error: string } | null>(null);
	/** The project's research map: its record and gap counts. */
	const [researchMap, setResearchMap] = useState<{ nodes: number; gaps: number } | { error: string } | null>(null);
	const hasResearch = Boolean(onOpenResearch);

	const takeDirection = useCallback((project: ProjectSummary) => {
		setDetail((d) => d && { ...d, project });
		setDirection(project.direction);
		setDirectionBase(project.directionVersion);
		setDirectionDirty(false);
		setDirectionStale(null);
	}, []);

	const takeState = useCallback((entry: MemoryEntry | null) => {
		setDetail((d) => d && { ...d, state: entry });
		setState(entry?.body ?? "");
		setStateBase(entry?.version ?? "");
		setStateDirty(false);
		setStateStale(null);
	}, []);

	const load = useCallback(async () => {
		try {
			const d = await api.project(id);
			setDetail(d);
			takeDirection(d.project);
			takeState(d.state);
		} catch (err) {
			setError(err instanceof ApiError ? err.message : "加载 project 失败");
		}
	}, [id, takeDirection, takeState]);

	useEffect(() => {
		setDetail(null);
		void load();
	}, [load]);

	useEffect(() => {
		if (showGraph) return;
		let live = true;
		void fetchGraph(id).then((found) => live && setGraph("graph" in found ? { view: found.graph } : "error" in found ? { error: found.error } : null));
		return () => {
			live = false;
		};
	}, [id, showGraph]);

	useEffect(() => {
		if (!hasResearch) return;
		let live = true;
		api.researchRecords({ kind: "project", projectId: id }).then(
			(r) => live && setResearch({ records: r.records.length, pending: [...r.records, ...r.latest].filter((x) => x.status === "proposed").length }),
			(err: unknown) => live && setResearch({ error: err instanceof ApiError ? err.message : "加载失败" }),
		);
		api.researchMap({ kind: "project", projectId: id }).then(
			(m) => live && setResearchMap({ nodes: m.nodes.length, gaps: m.gaps.length }),
			(err: unknown) => live && setResearchMap({ error: err instanceof ApiError ? err.message : "加载失败" }),
		);
		return () => {
			live = false;
		};
	}, [id, hasResearch]);

	async function run<T>(kind: NonNullable<typeof busy>, fn: () => Promise<unknown>, onStale?: (current: T | null) => void) {
		setBusy(kind);
		setError(null);
		try {
			await fn();
		} catch (err) {
			const copy = staleCopy<T>(err);
			if (copy && onStale) onStale(copy.current);
			else setError(err instanceof ApiError ? err.message : "操作失败");
		} finally {
			setBusy(null);
		}
	}

	function saveDirection(ifVersion = directionBase) {
		void run<ProjectSummary>(
			"direction",
			async () => {
				takeDirection((await api.updateProject(id, { direction, ifVersion })).project);
				onChanged();
			},
			(current) => setDirectionStale({ current }),
		);
	}

	function saveState(ifVersion = stateBase) {
		void run<MemoryEntry>("state", async () => takeState((await api.saveState(id, state, ifVersion)).state), (current) => setStateStale({ current }));
	}

	if (!detail) {
		return (
			<main className="chat project">
				<header className="chat-head page-head">
					<button className="ghost sidebar-toggle icon-btn" aria-label="菜单" onClick={onMenu}>
						<MenuIcon size={22} />
					</button>
					<div className="titles">
						<span className="title">Project</span>
					</div>
				</header>
				<p className="hint" style={{ padding: 14 }}>
					{error ?? "加载中…"}
				</p>
			</main>
		);
	}

	if (showGraph) return <ProjectGraph projectId={id} onBack={() => setShowGraph(false)} />;

	const { project } = detail;
	const known = graph && "view" in graph ? graph.view.nodes.filter((n) => n.state === "known").length : 0;
	return (
		<main className="chat project">
			<header className="chat-head page-head">
				<button className="ghost sidebar-toggle icon-btn" aria-label="菜单" onClick={onMenu}>
					<MenuIcon size={22} />
				</button>
				<div className="titles">
					<span className="title">{project.title}</span>
					<span className="sub">
						{project.profile} · {detail.sessions.length} 个对话 · 更新于 {when(project.updatedAt)}
						{project.archived && " · 已归档"}
					</span>
				</div>
				{!project.archived && <button onClick={() => onNewSession(project.profile, project.id)}>新建对话</button>}
			</header>
			{error && (
				<p className="banner error">
					{error}{" "}
					<button className="ghost" onClick={() => setError(null)}>
						关闭
					</button>
				</p>
			)}
			<div className="project-body">
				<section className="loop-view">
					{graph && (
						<button className="loop-card kg-entry" onClick={() => setShowGraph(true)}>
							<span className="loop-title">知识图谱</span>
							<span className="loop-meta">{"view" in graph ? `已掌握 ${known}/${graph.view.nodes.length}` : "图谱生成失败"}</span>
						</button>
					)}
					{onOpenResearch && (
						<a
							className="loop-card kg-entry research-entry"
							href={researchPageHref({ kind: "project", projectId: project.id })}
							onClick={(e) => {
								if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
								e.preventDefault();
								onOpenResearch();
							}}
						>
							<span className="loop-title">研究记录</span>
							<span className="loop-meta">
								{research === null ? "加载中…" : "error" in research ? "加载失败" : `${research.records} 条记录 · ${research.pending} 条待确认`}
							</span>
						</a>
					)}
					{onOpenResearch && (
						<a
							className="loop-card kg-entry research-entry"
							href={researchPageHref({ kind: "project", projectId: project.id }, undefined, "map")}
							onClick={(e) => {
								if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
								e.preventDefault();
								onOpenResearch("map");
							}}
						>
							<span className="loop-title">研究地图</span>
							<span className="loop-meta">{researchMap === null ? "加载中…" : "error" in researchMap ? "加载失败" : `${researchMap.nodes} 个节点 · ${researchMap.gaps} 个缺口`}</span>
						</a>
					)}
					<div>
						<h4>方向</h4>
						<div className="form">
							{detail.directionFields.map((field) => (
								<label key={field.name}>
									{field.label}
									{field.required ? " · 必填" : " · 可跳过"}
									<textarea
										className="memory-body short"
										value={direction[field.name] ?? ""}
										rows={3}
										placeholder={field.hint}
										onChange={(e) => (setDirection((v) => ({ ...v, [field.name]: e.target.value })), setDirectionDirty(true))}
									/>
								</label>
							))}
						</div>
						{directionStale && (
							<StaleNotice
								onReload={() => (directionStale.current ? takeDirection(directionStale.current) : void load())}
								onOverwrite={() => saveDirection(directionStale.current?.directionVersion)}
							/>
						)}
						<div className="row">
							<button disabled={busy !== null || !directionDirty} onClick={() => saveDirection()}>
								{busy === "direction" ? "保存中…" : "保存方向"}
							</button>
						</div>
					</div>

					<div>
						<h4>状态</h4>
						{detail.state && (
							<p className="hint">{[detail.state.updated && `更新于 ${detail.state.updated}`, `${detail.state.bytes} / 8192 字节`].filter(Boolean).join(" · ")}</p>
						)}
						<textarea
							className="memory-body"
							value={state}
							rows={18}
							onChange={(e) => (setState(e.target.value), setStateDirty(true))}
						/>
						{stateStale && (
							<StaleNotice onReload={() => takeState(stateStale.current)} onOverwrite={() => saveState(stateStale.current?.version ?? "")} />
						)}
						<div className="row">
							<button disabled={busy !== null || !stateDirty || !state.trim()} onClick={() => saveState()}>
								{busy === "state" ? "保存中…" : "保存状态"}
							</button>
						</div>
					</div>

					<details>
						<summary>日志{detail.log ? `（${detail.log.bytes} 字节）` : ""}</summary>
						<pre className="loop-summary log-body">{detail.log?.body ?? "暂无记录"}</pre>
					</details>

					<div>
						<h4>对话</h4>
						<div className="project-sessions-list">
							{detail.sessions.length === 0 && <p className="hint">暂无对话</p>}
							{detail.sessions.map((s) => (
								<button key={s.id} className="loop-card" onClick={() => onOpenSession(s.id)}>
									<span className="loop-title">{s.title ?? "新对话"}</span>
									<span className="loop-meta">
										{when(s.updatedAt)}
										{s.archived && " · 已归档"}
									</span>
								</button>
							))}
						</div>
					</div>

					<div className="row">
						<button
							className="ghost"
							disabled={busy !== null}
							onClick={() => {
								if (!project.archived && !confirm("归档此 project？对话和状态将保留。")) return;
								void run("archive", async () => {
									const { project: next } = await api.updateProject(project.id, { archived: !project.archived });
									setDetail((d) => d && { ...d, project: next });
									onChanged();
								});
							}}
						>
							{project.archived ? "取消归档" : "归档 project"}
						</button>
					</div>
				</section>
			</div>
		</main>
	);
}

/** The form a project starts from: a title, on one fixed profile. The direction is asked for in conversation. */
export function NewProject({ profile, onClose, onCreated }: { profile: string; onClose: () => void; onCreated: (project: ProjectSummary) => void }) {
	const [title, setTitle] = useState("");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	async function submit() {
		if (!title.trim()) return setError("名称不能为空");
		setBusy(true);
		setError(null);
		try {
			const { project } = await api.createProject({ profile, title: title.trim() });
			onCreated(project);
		} catch (err) {
			setError(err instanceof ApiError ? err.message : "创建失败");
		} finally {
			setBusy(false);
		}
	}

	return (
		<div className="modal" onClick={onClose}>
			<div className="modal-card loop-form" onClick={(e) => e.stopPropagation()}>
				<header>
					<h3>在 {profile} 下新建 project</h3>
					<button className="ghost" onClick={onClose}>
						关闭
					</button>
				</header>
				<div className="form">
					<label>
						名称
						<input value={title} onChange={(e) => setTitle(e.target.value)} />
					</label>
					{error && <p className="banner error">{error}</p>}
					<div className="row">
						<button onClick={() => void submit()} disabled={busy}>
							{busy ? "创建中…" : "创建"}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}
